/**
 * The public view of a v3 snapshot.
 *
 * Written for someone on the other side of the table: every account is shown,
 * both directions, with each source beside the one relied on and every figure
 * traceable to a month. Rows Adwin is putting in issue say what is being asked
 * for; rows that are not in issue say nothing.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Search, AlertTriangle } from 'lucide-react';
import type { SharedV3Payload } from '../../lib/raketechShareV3';
import type { ClaimSource } from '../../types/raketechFinalReportV3.types';
import SharedFocusView from './SharedFocusView';

const SOURCE_LABEL: Record<ClaimSource, string> = {
  june_recon: 'June 2025 file, Adwin earnings',
  june_reported: 'June 2025 file, Reported (EUR)',
  raketech_restated: 'July 2026 report',
  synced: 'Operator backend, net of 5%',
  manual_check: 'Manual backend check, net of 5%',
};

const fmt = (v: number) =>
  new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonth = (m: string) => {
  const [y, mo] = (m || '').split('-');
  return MONTHS[parseInt(mo, 10) - 1] ? `${MONTHS[parseInt(mo, 10) - 1]} ${y}` : m;
};

/** Written out rather than interpolated so Tailwind keeps the classes. */
const ACTION_TONE: Record<string, string> = {
  Evidence: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  Never: 'border-red-500/30 bg-red-500/10 text-red-400',
  Mapping: 'border-sky-500/30 bg-sky-500/10 text-sky-400',
  To: 'border-violet-500/30 bg-violet-500/10 text-violet-400',
  Deal: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
};

const actionTone = (action: string) => ACTION_TONE[action.split(' ')[0]] || ACTION_TONE.Mapping;

type Filter = 'all' | 'evidence' | 'never' | 'mapping' | 'source' | 'deal' | 'short' | 'over';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All accounts' },
  { id: 'evidence', label: 'Evidence needed' },
  { id: 'never', label: 'Never reported' },
  { id: 'mapping', label: 'Mapping needed' },
  { id: 'source', label: 'To reconcile at source' },
  { id: 'deal', label: 'Deal terms needed' },
  { id: 'short', label: 'Their figure is higher' },
  { id: 'over', label: 'Invoiced above their figure' },
];

interface Props {
  payload: SharedV3Payload;
  title: string;
  description: string | null;
  createdAt: string;
  dark: boolean;
}

/** A focused snapshot saved from the PA = NO or Write-downs tab carries the
 *  facts those tabs are built on; it is shown as the tab showed it. Snapshots
 *  saved before those facts were recorded fall through to the general view. */
const isFocused = (p: SharedV3Payload): p is SharedV3Payload & { focus: NonNullable<SharedV3Payload['focus']> } =>
  !!p.focus && p.rows.length > 0 && p.rows.every((r) => r.facts?.writeDown !== undefined && r.facts?.theirFigure !== undefined);

const SharedClaimV3: React.FC<Props> = (props) => {
  if (isFocused(props.payload)) return <SharedFocusView {...props} payload={props.payload} />;
  return <SharedClaimGeneral {...props} />;
};

const SharedClaimGeneral: React.FC<Props> = ({ payload, title, description, createdAt, dark }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const card = dark ? 'bg-[hsl(225,15%,10%)] border-[hsl(225,15%,18%)]' : 'bg-white border-gray-200';
  const head = dark ? 'bg-[hsl(225,15%,8%)]' : 'bg-gray-100';
  const primary = dark ? 'text-white' : 'text-gray-900';
  const secondary = dark ? 'text-zinc-400' : 'text-gray-500';
  const muted = dark ? 'text-zinc-500' : 'text-gray-400';
  const value = dark ? 'text-zinc-300' : 'text-gray-700';
  const divider = dark ? 'divide-[hsl(225,15%,13%)]' : 'divide-gray-100';
  const hover = dark ? 'hover:bg-[hsl(225,15%,12%)]' : 'hover:bg-gray-50';
  const input = dark
    ? 'bg-[hsl(225,15%,12%)] border-[hsl(225,15%,20%)] text-white placeholder-zinc-500'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';
  const drill = dark ? 'bg-[hsl(225,15%,8%)] border-[hsl(225,15%,15%)]' : 'bg-gray-50 border-gray-200';

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payload.rows.filter((r) => {
      if (q && ![r.accountId, r.partner, r.username, ...r.brands, ...r.geos, ...r.acIds]
        .some((f) => String(f || '').toLowerCase().includes(q))) return false;
      switch (filter) {
        case 'evidence': return r.actions.some((a) => a.startsWith('Evidence'));
        case 'never': return r.actions.some((a) => a.startsWith('Never'));
        case 'mapping': return r.actions.some((a) => a.startsWith('Mapping'));
        case 'source': return r.actions.some((a) => a.startsWith('To reconcile'));
        case 'deal': return r.actions.some((a) => a.startsWith('Deal terms'));
        case 'short': return r.difference > 1;
        case 'over': return r.difference < -1;
        default: return true;
      }
    });
  }, [payload.rows, search, filter]);

  const shown = useMemo(() => rows.reduce((t, r) => ({
    invoiced: t.invoiced + r.invoiced, earned: t.earned + r.earned, diff: t.diff + r.difference,
  }), { invoiced: 0, earned: 0, diff: 0 }), [rows]);

  const toggle = (key: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const Stat: React.FC<{ label: string; value: string; hint: string; tone?: string }> =
    ({ label, value: v, hint, tone }) => (
      <div className={`rounded-xl border p-4 ${card}`}>
        <div className={`text-[11px] uppercase tracking-wide mb-1 ${muted}`}>{label}</div>
        <div className={`text-lg font-semibold ${tone || primary}`}>{v}</div>
        <div className={`text-[11px] mt-1 ${secondary}`}>{hint}</div>
      </div>
    );

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="mb-6">
        <h1 className={`text-xl font-semibold ${primary}`}>{title}</h1>
        <p className={`text-xs mt-1 ${muted}`}>
          {fmtMonth(payload.period.from)} to {fmtMonth(payload.period.to)} · {payload.totals.accounts} accounts ·
          prepared {new Date(createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
        {description && <p className={`text-sm mt-3 max-w-4xl leading-relaxed ${value}`}>{description}</p>}
      </div>

      <div className={`rounded-xl border p-4 mb-5 text-xs leading-relaxed ${card} ${value}`}>
        {payload.focus ? (
          <>
            <p className={`font-semibold mb-2 ${primary}`}>{payload.focus.title}</p>
            <p className="mb-2">{payload.focus.note}</p>
            <p className={muted}>Selection: {payload.focus.selection}. Figures are Raketech's own, from its June 2025
              reconciliation, its July 2026 report and its monthly Commission reports, set beside what Adwin invoiced
              on those reports. Open an account for the month-by-month detail.</p>
          </>
        ) : (
          <>
            <p className="mb-2">
              This is Adwin's position account by account on the reference data Raketech identified as correct: the
              <strong> Adwin earnings</strong> column of the June 2025 reconciliation, together with Raketech's July 2026
              report, Raketech's monthly Commission reports, the invoices Adwin raised on those reports, operator backend
              data where access has been granted, and Affiliation Cloud's reported figures. Every figure held for an
              account is shown beside the one relied on, so each amount can be traced to its source and tested
              individually. Where a source is silent it is shown as silent, not as zero.
            </p>
            <p className={muted}>
              A working reconciliation position for review. It is not a payment demand and does not restate invoice
              no. 20318. It will move as the outstanding items are provided. The flat fee under clause 4.3.1 and the
              amounts already part-paid against the confirmed underpayment are tracked separately and are not in it.
            </p>
          </>
        )}
        {payload.attribution && (
          <p className={`mt-2 ${muted}`}>
            Adwin's invoicing is placed on accounts line by line: {payload.attribution.lines.toLocaleString('en-GB')} lines
            of invoice detail, EUR {fmt(payload.attribution.all)} in all, of which EUR {fmt(payload.attribution.attributed)} sits
            on the accounts below and EUR {fmt(payload.attribution.unattributed)} names no account either party lists
            {payload.attribution.balanced ? '; the two add back to the total' : ''}. Each line is placed by the deal
            code it carries first, and by its brand only where no code identifies it. EUR {fmt(payload.attribution.judgedTotal)}
            across {payload.attribution.judgedLines} lines rests on account size where a code sits on more than one account
            and nothing else separates them; those lines are marked in the drilldown.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat label="Invoiced by Adwin" value={`EUR ${fmt(payload.totals.invoiced)}`} hint="On Raketech's monthly reports" />
        <Stat label="Raketech's own figures" value={`EUR ${fmt(payload.totals.earned)}`} hint="Same accounts, best of their sources" />
        <Stat
          label="Their figure is higher"
          value={`EUR ${fmt(payload.totals.shortGross)}`}
          hint={`On ${payload.totals.shortAccounts} accounts`}
          tone="text-red-400"
        />
        <Stat
          label="Invoiced above their figure"
          value={`EUR ${fmt(payload.totals.overGross)}`}
          hint={`On ${payload.totals.overAccounts} accounts`}
          tone="text-emerald-400"
        />
      </div>

      <div className={`rounded-xl border p-4 mb-5 text-xs leading-relaxed ${card}`}>
        <div className={`font-medium mb-2 ${primary}`}>How rows are marked</div>
        <p className={`mb-1.5 ${value}`}>
          <span className="px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-400 mr-2">Evidence needed</span>
          Raketech's June 2025 file credits the account with less than its own <em>Reported</em> column says its monthly
          reports paid out on, which is what Adwin invoiced against at the time: {payload.marks.writtenDownAccounts}{' '}
          accounts, EUR {fmt(payload.totals.reportedWriteDown)}, of which EUR{' '}
          {fmt(payload.totals.reportedWriteDownCommented)} is annotated in Raketech's own comment column. The mark is
          set on those two columns alone, so it stands whether or not Adwin values the account on the reported figure.
          A further {payload.marks.restatedAccounts} accounts are marked where the July 2026 report carries less than
          Raketech's own June 2025 file.
        </p>
        <p className={`mb-1.5 ${value}`}>
          <span className="px-1.5 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-400 mr-2">Never reported</span>
          Earnings in Raketech's files for which no invoice was ever raised, because no monthly report was issued for
          them: {payload.marks.neverAccounts} accounts, EUR {fmt(payload.marks.neverAmount)}. In each case the brand
          carries no invoicing under any account id in the month concerned.
        </p>
        <p className={`mb-1.5 ${value}`}>
          <span className="px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-400 mr-2">Mapping needed</span>
          Months invoiced on Raketech's own report that no Raketech source describes, and the same re-mapping seen from
          the other side: EUR {fmt(payload.marks.remapAmount)} on {payload.marks.remapAccounts} accounts Adwin raised no
          invoice against, because the invoice for that brand and month was raised under another identifier. The two are
          the same money viewed from opposite sides.
        </p>
        <p className={`mb-1.5 ${value}`}>
          <span className="px-1.5 py-0.5 rounded border border-violet-500/30 bg-violet-500/10 text-violet-400 mr-2">To reconcile at source</span>
          {payload.marks.backendAccounts} accounts, EUR {fmt(payload.marks.backendAmount)}, are valued above every
          figure Raketech puts on them, from data Adwin holds from the operator backend or a manual check of it. These
          are not presented as agreed: they are the rows only source data can settle, and Adwin will produce its
          exports for each. Raketech's own comment on several of them is shown in the drilldown.
        </p>
        <p className={value}>
          <span className="px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 mr-2">Deal terms needed</span>
          {payload.marks.dealBasisAccounts} accounts, EUR {fmt(payload.marks.dealBasisAmount)}, were reported to Adwin
          as paying a fixed amount per acquisition and no revenue share, while the operator account was earning revenue
          share over the same period. Clause 4.8 measures the fee by what the Gaming Operator pays or owes Raketech on
          Adwin's New Users in a Partner Account, and clause 4.3.2 gives Adwin that Net Revenue less 5%, so the
          reporting basis does not settle these rows. The Partner Account Agreement for each has been requested.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${muted}`} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search account id, partner, brand or geo"
            className={`w-full pl-9 pr-3 py-2 text-xs rounded-lg border outline-none ${input}`}
          />
        </div>
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
              filter === f.id
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
                : `${card} ${secondary}`
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className={`rounded-xl border overflow-hidden ${card}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className={head}>
              <tr className={muted}>
                <th className="w-8" />
                <th className="px-3 py-2.5 text-left font-medium">Account</th>
                <th className="px-3 py-2.5 text-left font-medium">Partner</th>
                <th className="px-3 py-2.5 text-left font-medium">Brand</th>
                <th className="px-3 py-2.5 text-right font-medium">Invoiced</th>
                <th className="px-3 py-2.5 text-right font-medium">Raketech's figure</th>
                <th className="px-3 py-2.5 text-left font-medium">Source used</th>
                <th className="px-3 py-2.5 text-right font-medium">Difference</th>
                <th className="px-3 py-2.5 text-left font-medium">Action needed</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${divider}`}>
              {rows.map((row) => (
                <React.Fragment key={row.key}>
                  <tr className={`${hover} cursor-pointer`} onClick={() => toggle(row.key)}>
                    <td className="pl-3">
                      {open.has(row.key)
                        ? <ChevronDown className={`h-3.5 w-3.5 ${muted}`} />
                        : <ChevronRight className={`h-3.5 w-3.5 ${muted}`} />}
                    </td>
                    <td className={`px-3 py-2.5 font-mono ${value}`}>
                      {row.accountId}
                      {row.geos.length > 0 && <span className={`ml-2 ${muted}`}>{row.geos.join(' ')}</span>}
                      {payload.focus && row.facts && (
                        <div className={`font-sans text-[10px] mt-0.5 ${muted}`}>
                          {payload.focus.kind === 'pa_no_players' && (
                            <>
                              PA = {row.facts.pa.toUpperCase()}
                              {row.facts.created ? ` · created ${row.facts.created.slice(0, 10)}` : ''}
                              {row.facts.status ? ` · ${row.facts.status}` : ''}
                              {row.facts.firstFtd ? ` · FTDs ${fmtMonth(row.facts.firstFtd)} – ${fmtMonth(row.facts.lastFtd || row.facts.firstFtd)} (${row.facts.ftds})` : ' · no FTDs on the monthly reports'}
                              {row.facts.exactMatch ? ' · Raketech\'s figure equals Adwin\'s invoicing to the cent' : ''}
                            </>
                          )}
                          {payload.focus.kind === 'write_downs' && (
                            <>
                              reported at the time {fmt(row.reported)}
                              {row.reportedVsCredited > 1 ? ` · credited ${fmt(row.reportedVsCredited)} less in June 2025` : ''}
                              {row.facts.tailWrittenDown > 50 ? ` · July 2026 report ${fmt(row.facts.tailWrittenDown)} below the monthly reports after Nov 2024` : ''}
                              {row.facts.ownData ? '' : ' · Adwin holds no figure of its own'}
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className={`px-3 py-2.5 ${value}`}>{row.partner}</td>
                    <td className={`px-3 py-2.5 ${value}`}>{row.brands.join(' / ')}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums ${value}`}>{fmt(row.invoiced)}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums ${value}`}>{fmt(row.earned)}</td>
                    <td className={`px-3 py-2.5 ${muted}`}>{SOURCE_LABEL[row.chosenSource]}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${
                      row.difference > 1 ? 'text-red-400' : row.difference < -1 ? 'text-emerald-400' : muted
                    }`}>
                      {fmt(row.difference)}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {row.actions.map((a) => (
                          <span
                            key={a}
                            title={a}
                            className={`px-1.5 py-0.5 rounded text-[10px] border ${actionTone(a)}`}
                          >
                            {a.split(' - ')[0]}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                  {open.has(row.key) && (
                    <tr>
                      <td colSpan={9} className={`px-4 py-4 border-y ${drill}`}>
                        {row.actions.length > 0 && (
                          <div className="mb-4 space-y-1">
                            {row.actions.map((a) => (
                              <div key={a} className={`flex items-start gap-2 text-[11px] ${value}`}>
                                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-amber-400" />
                                <span>{a}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <div className={`text-[11px] uppercase tracking-wide mb-2 ${muted}`}>
                              Every figure held for this account
                            </div>
                            <div className="space-y-1">
                              {row.candidates.map((c) => (
                                <div key={c.source} className="flex items-center justify-between text-[11px]">
                                  <span className={c.source === row.chosenSource ? 'text-amber-400' : secondary}>
                                    {SOURCE_LABEL[c.source]}
                                    {c.source === row.chosenSource && ' — relied on'}
                                    {c.marginApplied && <span className={muted}> (net of 5%)</span>}
                                  </span>
                                  <span className={`tabular-nums ${c.source === row.chosenSource ? 'text-amber-400' : value}`}>
                                    {fmt(c.amount)}
                                  </span>
                                </div>
                              ))}
                              {row.reported > 0 && (
                                <div className="flex items-center justify-between text-[11px]">
                                  <span className={secondary}>Reported to Adwin at the time</span>
                                  <span className={`tabular-nums ${value}`}>{fmt(row.reported)}</span>
                                </div>
                              )}
                              {row.reportedVsCredited > 1 && (
                                <div className="flex items-center justify-between text-[11px]">
                                  <span className={secondary}>Credited below what was reported</span>
                                  <span className="tabular-nums text-amber-400">{fmt(row.reportedVsCredited)}</span>
                                </div>
                              )}
                            </div>
                            {row.chosenSource === 'june_reported' && (
                              <p className={`text-[11px] mt-2 ${muted}`}>
                                Raketech's <em>Reported (EUR)</em> is an account total with no monthly breakdown behind
                                it. The months below spread that total across the shape of the months Raketech credited.
                              </p>
                            )}
                            {row.dealBasis === 'acquisition_only' && (
                              <p className={`text-[11px] mt-2 ${muted}`}>
                                Raketech reported this account to Adwin as paying per acquisition at a fixed price and
                                no revenue share, while the account was earning revenue share over the same period.
                                Clause 4.8 measures the fee by what the Gaming Operator pays or owes Raketech on
                                Adwin's New Users in a Partner Account, and clause 4.3.2 gives Adwin that Net Revenue
                                less 5%, so the reporting basis does not settle the row. The Partner Account
                                Agreement for it has been requested.
                              </p>
                            )}
                            {row.concession && (
                              <p className={`text-[11px] mt-2 ${muted}`}>
                                Held to Adwin's own position: {row.concession}
                              </p>
                            )}
                            {row.earned > 0 && row.candidates.every((c) => c.amount <= 0) && (
                              <p className={`text-[11px] mt-2 ${muted}`}>
                                The figures above cover the reconciled period, March 2023 to November 2024, which the
                                June 2025 file reported on. This account carries earnings only after it, so it is
                                valued on the months set out below, from Raketech's July 2026 report.
                              </p>
                            )}
                          </div>

                          <div>
                            <div className={`text-[11px] uppercase tracking-wide mb-2 ${muted}`}>Coverage</div>
                            {row.gapMonths > 0 ? (
                              <div className={`text-[11px] space-y-1 ${value}`}>
                                <div>
                                  {row.gapMonths} month{row.gapMonths === 1 ? '' : 's'} invoiced, EUR {fmt(row.gapInvoiced)},
                                  which no Raketech source describes.
                                </div>
                                {row.gapReportedElsewhere > 0 && (
                                  <div className={secondary}>
                                    EUR {fmt(row.gapReportedElsewhere)} of that appears in Raketech's files under a
                                    different account id for the same brand.
                                  </div>
                                )}
                                {row.gapAbsent > 0 && (
                                  <div className={secondary}>
                                    EUR {fmt(row.gapAbsent)} does not appear in Raketech's files at all.
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className={`text-[11px] ${secondary}`}>
                                Every month invoiced is described by at least one Raketech source.
                              </div>
                            )}
                            {row.comments.length > 0 && (
                              <div className="mt-3">
                                <div className={`text-[11px] uppercase tracking-wide mb-1 ${muted}`}>
                                  Raketech's own comment
                                </div>
                                {row.comments.map((c, i) => (
                                  <div key={i} className={`text-[11px] italic ${value}`}>"{c}"</div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className={`text-[11px] uppercase tracking-wide mb-2 ${muted}`}>Month by month</div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px]">
                            <thead className={muted}>
                              <tr>
                                <th className="px-2 py-1 text-left font-medium">Month</th>
                                <th className="px-2 py-1 text-right font-medium">Invoiced</th>
                                <th className="px-2 py-1 text-right font-medium">FTDs</th>
                                <th className="px-2 py-1 text-right font-medium">Relied on</th>
                                <th className="px-2 py-1 text-right font-medium">Difference</th>
                                <th className="px-2 py-1 text-right font-medium">June file</th>
                                <th className="px-2 py-1 text-right font-medium">Reported</th>
                                <th className="px-2 py-1 text-right font-medium">July report</th>
                                <th className="px-2 py-1 text-right font-medium">Backend</th>
                                <th className="px-2 py-1 text-right font-medium">AC</th>
                              </tr>
                            </thead>
                            <tbody className={`divide-y ${divider}`}>
                              {row.months.map(([month, invoiced, ftds, earned, june, reported, restated, synced, ac]) => {
                                const diff = Math.round((earned - invoiced) * 100) / 100;
                                return (
                                  <tr key={month}>
                                    <td className={`px-2 py-1 ${value}`}>{fmtMonth(month)}</td>
                                    <td className={`px-2 py-1 text-right tabular-nums ${value}`}>{invoiced ? fmt(invoiced) : '—'}</td>
                                    <td className={`px-2 py-1 text-right tabular-nums ${muted}`}>{ftds || '—'}</td>
                                    <td className={`px-2 py-1 text-right tabular-nums ${value}`}>{earned ? fmt(earned) : '—'}</td>
                                    <td className={`px-2 py-1 text-right tabular-nums ${
                                      diff > 1 ? 'text-red-400' : diff < -1 ? 'text-emerald-400' : muted
                                    }`}>{fmt(diff)}</td>
                                    <td className={`px-2 py-1 text-right tabular-nums ${muted}`}>{june ? fmt(june) : '—'}</td>
                                    <td className={`px-2 py-1 text-right tabular-nums ${muted}`}>{reported ? fmt(reported) : '—'}</td>
                                    <td className={`px-2 py-1 text-right tabular-nums ${muted}`}>{restated ? fmt(restated) : '—'}</td>
                                    <td className={`px-2 py-1 text-right tabular-nums ${muted}`}>{synced ? fmt(synced) : '—'}</td>
                                    <td className={`px-2 py-1 text-right tabular-nums ${muted}`}>{ac ? fmt(ac) : '—'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot className={head}>
              <tr className={value}>
                <td />
                <td className="px-3 py-2.5 font-medium" colSpan={3}>{rows.length} accounts shown</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(shown.invoiced)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmt(shown.earned)}</td>
                <td />
                <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${
                  shown.diff > 0 ? 'text-red-400' : 'text-emerald-400'
                }`}>{fmt(shown.diff)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <p className={`text-[11px] mt-4 ${muted}`}>
        All amounts in EUR. A positive difference means Raketech's own figure exceeds what Adwin invoiced for that
        account. Where Raketech's report carries several of Adwin's deals under a single account id, that id appears
        once per deal group and Raketech's figure for it is apportioned between the groups on invoiced share, so that
        no figure is counted twice. Invoice lines matching more than one account on the deal label are deducted once
        only, against the account whose offer names the deal.
      </p>
    </div>
  );
};

export default SharedClaimV3;
